/* =========================================================================
   OhMy! Reservierungs-Timer — see snippets/ohmy-cart-timer.liquid.

   One deadline in localStorage ("om-hold-until"), shared by every tab: back
   to the full N minutes whenever the item count goes up (new item or higher
   quantity), never extended by mere activity. When it passes with items in
   the cart, the cart is really cleared (/cart/clear.js) and a toast offers to
   put the items back (snapshot in "om-hold-undo", kept 30 days).

   The snapshot is a standing offer: closing the toast only hides it for this
   visit; every later visit shows the reminder again until the items are put
   back, discarded, or already sit in the cart again.

   A checkout click stops the clearing altogether ("om-hold-checkout") until
   the next add or an empty cart (order placed): checkout pages run without
   the theme, and nobody should lose a cart they are paying for.

   The countdown is drawn inside the Section Store cart app (shadow DOM,
   above .cart-body) and kept there with a MutationObserver, because the app
   re-renders its panel. All cart changes go through the app's public API
   (window.SectionStoreCart v1: subscribe, refresh, open, onBeforeCheckout);
   without the app it falls back to the Ajax API and a page reload.
   ========================================================================= */
(() => {
  'use strict';

  const cfg = document.getElementById('OmCartHold');
  if (!cfg) return;

  const T = cfg.dataset;
  const MS = (+T.minutes || 15) * 60e3;
  const LAST = 2 * 60e3;                          // last two minutes: say what happens at zero
  const KEEP_MS = 30 * 24 * 60 * 60e3;            // how long the reminder keeps offering the items
  const DESIGN = T.design === 'true';
  const K_UNTIL = 'om-hold-until', K_UNDO = 'om-hold-undo', K_LOCK = 'om-hold-lock', K_NOTICE = 'om-hold-notice', K_CHECKOUT = 'om-hold-checkout', K_REMIND = 'om-hold-remind-off';
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const onCartPage = /\/cart\/?$/.test(location.pathname);

  const store = {
    get(k, s = localStorage) { try { return s.getItem(k); } catch { return null; } },
    set(k, v, s = localStorage) { try { s.setItem(k, v); } catch { /* private mode */ } },
    del(k, s = localStorage) { try { s.removeItem(k); } catch { /* private mode */ } }
  };

  let api = null;
  let count = +T.count || 0;
  let memUntil = 0;                               // fallback when storage is blocked
  let expiring = false;

  const getUntil = () => +store.get(K_UNTIL) || memUntil;
  function setUntil(t) { memUntil = t; t ? store.set(K_UNTIL, String(t)) : store.del(K_UNTIL); }

  /* ---------- checkout ---------- */
  function toCheckout() { if (count) store.set(K_CHECKOUT, '1'); }
  const inCheckout = () => !!store.get(K_CHECKOUT);
  document.addEventListener('click', (e) => {
    const el = e.target.closest && e.target.closest('a[href*="/checkout"], button[name="checkout"], input[name="checkout"]');
    if (el) toCheckout();
  }, true);
  document.addEventListener('submit', (e) => {
    if (e.submitter && e.submitter.name === 'checkout') toCheckout();
  }, true);

  /* ---------- countdown inside the app's drawer ---------- */
  const CSS = `
    .om-hold{display:flex;align-items:center;justify-content:center;gap:8px;padding:9px 16px;background:#C1E1EE;color:#2F3E4E;font-family:var(--font-body,inherit);font-size:13.5px;line-height:1.35;text-align:center}
    .om-hold[hidden]{display:none}
    .om-hold svg{flex:none;width:16px;height:16px}
    .om-hold b{font-weight:600;font-variant-numeric:tabular-nums}`;
  const ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="13" r="8"/><path d="M12 9v4l2.5 2.5M9.5 2.5h5"/></svg>';

  let host = null, bar = null, mo = null;
  const shadow = () => host && host.shadowRoot;

  function mount() {
    const sr = shadow();
    const aside = sr && sr.querySelector('aside.cart-drawer');
    const body = aside && aside.querySelector('.cart-body');
    if (!body) return null;
    if (!sr.querySelector('style[data-om-hold]')) {
      const s = document.createElement('style');
      s.dataset.omHold = '';
      s.textContent = CSS;
      sr.append(s);
    }
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'om-hold';
      bar.setAttribute('role', 'timer');
      bar.innerHTML = ICON + '<span></span>';
    }
    if (bar.nextElementSibling !== body) body.before(bar);
    return bar;
  }

  const mmss = (ms) => {
    const s = Math.ceil(ms / 1000);
    return String(Math.floor(s / 60)).padStart(2, '0') + ':' + String(s % 60).padStart(2, '0');
  };

  function paint() {
    const until = getUntil();
    const open = api ? api.isOpen() : false;
    if (!open || !count || !until || inCheckout()) { if (bar) bar.hidden = true; return; }
    const el = mount();
    if (!el) return;
    const left = until - Date.now();
    if (left <= 0) { el.hidden = true; return; }
    const txt = el.lastChild;
    txt.textContent = '';
    if (left <= LAST) {
      const [a, b] = T.tLast.split('%T%');
      const t = document.createElement('b');
      t.textContent = mmss(left);
      txt.append(a, t, b || '');
    } else {
      const t = document.createElement('b');
      t.textContent = mmss(left);
      txt.append(T.tLabel + ' ', t);
    }
    el.setAttribute('aria-label', `${T.tLabel}: ${mmss(left)}`);
    el.hidden = false;
  }

  /* ---------- toast ---------- */
  const toast = document.getElementById('OmCartHoldToast');
  let toastT;
  // mode: 'undo' right after clearing, 'remind' on a later visit, none for plain info
  function say(msg, mode, ms) {
    if (!toast) return;
    toast.querySelector('[data-om-hold-msg]').textContent = msg;
    const u = toast.querySelector('[data-om-hold-undo]');
    u.textContent = mode === 'remind' ? T.tRemindUndo : T.tUndo;
    u.hidden = !mode;
    const d = toast.querySelector('[data-om-hold-discard]');
    d.textContent = T.tDiscard;
    d.hidden = mode !== 'remind';
    const mini = document.getElementById('OmMiniCart');
    toast.classList.toggle('is-lift', !!mini && !mini.hidden);   // phone: sit above the cart bar
    toast.hidden = false;
    clearTimeout(toastT);
    if (ms) toastT = setTimeout(() => { toast.hidden = true; }, ms);
  }
  if (toast) {
    toast.querySelector('[data-om-hold-close]').addEventListener('click', () => {
      toast.hidden = true;
      if (store.get(K_UNDO)) store.set(K_REMIND, '1', sessionStorage);   // back next visit
    });
    toast.querySelector('[data-om-hold-undo]').addEventListener('click', restore);
    toast.querySelector('[data-om-hold-discard]').addEventListener('click', () => {
      toast.hidden = true;
      store.del(K_UNDO);
    });
  }

  function readSnap() {
    let snap;
    try { snap = JSON.parse(store.get(K_UNDO)); } catch { snap = null; }
    if (!snap || !Array.isArray(snap.items) || !snap.items.length || Date.now() - snap.at > KEEP_MS) {
      store.del(K_UNDO);
      return null;
    }
    return snap;
  }

  /* ---------- cart calls ---------- */
  const post = (url, body) => fetch(ROOT + url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body || {})
  });
  const readCart = () => fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

  async function afterChange(open) {
    if (api) {
      await api.refresh();
      if (open) api.open();
    } else if (onCartPage) {
      location.reload();
    }
  }

  async function expire() {
    if (expiring) return;
    if (DESIGN || inCheckout()) return;
    const lock = +store.get(K_LOCK);
    if (lock && Date.now() - lock < 8000) return;  // another tab is on it
    expiring = true;
    store.set(K_LOCK, String(Date.now()));
    try {
      const cart = await readCart();
      const until = getUntil();
      if (!cart.item_count) { count = 0; setUntil(0); return; }
      if (until && until > Date.now()) return;     // another tab saw activity meanwhile

      const items = cart.items.map((i) => {
        const it = { id: i.variant_id, quantity: i.quantity };
        if (i.properties && Object.keys(i.properties).length) it.properties = i.properties;
        if (i.selling_plan_allocation) it.selling_plan = i.selling_plan_allocation.selling_plan.id;
        return it;
      });
      store.set(K_UNDO, JSON.stringify({ at: Date.now(), items }));
      store.del(K_REMIND, sessionStorage);
      await post('cart/clear.js');
      count = 0;
      setUntil(0);
      store.del(K_CHECKOUT);
      if (bar) bar.hidden = true;
      if (!api && onCartPage) store.set(K_NOTICE, '1', sessionStorage);
      await afterChange(false);
      if (api && api.isOpen()) api.close();         // the drawer would cover the toast
      say(T.tExpired, 'undo');
    } catch { /* network hiccup — next tick tries again */ } finally {
      store.del(K_LOCK);
      expiring = false;
    }
  }

  async function restore() {
    const snap = readSnap();
    toast.hidden = true;
    if (!snap) return;
    store.del(K_UNDO);
    const r = await post('cart/add.js', { items: snap.items });
    if (!r.ok) {                                    // e.g. one item sold out: add the rest one by one
      for (const it of snap.items) await post('cart/add.js', { items: [it] }).catch(() => {});
    }
    const cart = await readCart().catch(() => null);
    count = cart ? cart.item_count : count;
    if (count) { setUntil(Date.now() + MS); store.del(K_CHECKOUT); }
    say(T.tRestored, null, 4000);
    await afterChange(true);
  }

  /* ---------- clock ---------- */
  function tick() {
    if (!count) { if (bar) bar.hidden = true; return; }
    if (inCheckout()) return paint();              // went to checkout: never cleared
    const until = getUntil();
    if (!until) { setUntil(Date.now() + MS); return paint(); }
    if (until <= Date.now()) return expire();
    paint();
  }

  // Other tabs: a cleared cart or a restore changes the count here too.
  window.addEventListener('storage', (e) => {
    if (e.key === K_UNDO || (e.key === K_UNTIL && !e.newValue)) {
      readCart().then((c) => { count = c.item_count; if (api) api.refresh(); paint(); }).catch(() => {});
    } else if (e.key === K_UNTIL) paint();
  });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) tick(); });
  window.addEventListener('pageshow', (e) => { if (e.persisted) tick(); });

  /* ---------- wire up to the app ---------- */
  function boot(a) {
    if (api) return;
    api = a;
    host = document.querySelector('section-store--cart-block');
    if (host && host.shadowRoot) {
      mo = new MutationObserver(() => { if (bar && !bar.isConnected && api.isOpen()) paint(); });
      mo.observe(host.shadowRoot, { childList: true, subtree: true });
    }
    api.subscribe((cart) => {
      if (!cart) return;
      const was = count;
      count = cart.item_count;
      if (!count) { setUntil(0); store.del(K_CHECKOUT); }
      else if (count > was) { setUntil(Date.now() + MS); store.del(K_CHECKOUT); }   // every add restarts the full reservation
      else if (!getUntil()) setUntil(Date.now() + MS);
      paint();
    });
    api.onBeforeCheckout(() => { toCheckout(); return true; });
    window.addEventListener('section-store:cart:opened', paint);
    window.addEventListener('section-store:cart:closed', paint);
    paint();
  }

  if (store.get(K_NOTICE, sessionStorage)) {
    store.del(K_NOTICE, sessionStorage);
    say(T.tExpired, 'undo');
  }

  tick();                                         // also catches a deadline that passed while nobody was here

  // Later visit: offer the cleared items again, unless closed this visit or already back in the cart.
  (async () => {
    const snap = readSnap();
    if (!snap || expiring || !toast || !toast.hidden || store.get(K_REMIND, sessionStorage)) return;
    const cart = await readCart().catch(() => null);
    if (!cart || expiring || !toast.hidden) return;
    const inCart = new Set(cart.items.map((i) => i.variant_id));
    if (snap.items.every((it) => inCart.has(it.id))) { store.del(K_UNDO); return; }
    say(T.tRemind, 'remind');
  })();

  setInterval(tick, 1000);

  if (window.SectionStoreCart) window.SectionStoreCart.ready.then(() => boot(window.SectionStoreCart));
  else window.addEventListener('section-store:cart:ready', () => window.SectionStoreCart && boot(window.SectionStoreCart), { once: true });
})();
